import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  input,
  InputSignal,
  OnInit,
  Output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { get, isString } from 'lodash';
import { Editor, NgxEditorConfig, NgxEditorModule, toHTML, Toolbar } from 'ngx-editor';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomMatErrorComponent } from '../custom-mat-error/custom-mat-error.component';
import { HtmlPreviewComponent } from '../html-preview/html-preview.component';
import { ReadToWriteBase } from '../read-to-write/read-to-write-base.class';
import { EReadToWriteMode } from '../read-to-write/read-to-write.interface';
import { doInputGenerateVisibleText } from '../../../core/validators/text-editor-required.validator';

@Component({
  imports: [
    NgxEditorModule,
    ReactiveFormsModule,
    TranslateModule,
    NgClass,
    CustomMatErrorComponent,
    HtmlPreviewComponent,
  ],
  selector: 'app-text-editor',
  styleUrl: './text-editor.component.scss',
  templateUrl: './text-editor.component.html',
})
export class TextEditorComponent extends ReadToWriteBase<Record<string, unknown> | string | null> implements OnInit {
  public showToolbar: InputSignal<boolean> = input<boolean>(true);
  public isFormSubmitted: InputSignal<boolean> = input<boolean>(false);
  @Output() public focused: EventEmitter<void> = new EventEmitter();
  @Output() public focusedOut: EventEmitter<void> = new EventEmitter();

  public editor: Editor = new Editor({});
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];
  public readonly isToolbarShown = computed(() => this.showToolbar() && this.mode() === EReadToWriteMode.write);

  public readonly preview$: Observable<string> = this.formControlValue$.obs$.pipe(
    map((value) => (!value || typeof value === 'string' ? value || '' : toHTML(value))),
  );
  public readonly preview = toSignal(this.preview$, { initialValue: '' });
  public override readonly mode = computed(() => {
    if (this.isFormDataLoading()) {
      return EReadToWriteMode.formDataLoading;
    }

    if (this.shouldBeStuckInWriteMode() || this.modeIfEdit() === EReadToWriteMode.write) {
      return EReadToWriteMode.write;
    }

    return doInputGenerateVisibleText(this.preview()) ? EReadToWriteMode.read : EReadToWriteMode.noData;
  });

  private focusedOnce = false;

  constructor(private readonly eRef: ElementRef) {
    super();
  }

  public onFocus(): void {
    this.focused.emit();
    this.focusedOnce = true;
  }

  @HostListener('document:click', ['$event'])
  public clickOutside(event: Event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.eRef.nativeElement?.contains(event.target)) {
      return;
    }

    this.focusedOut.emit();
    event.stopPropagation();

    if (this.focusedOnce) {
      this.formControl().markAsDirty();
    }

    if (
      (get(this.formControl(), 'value.content.length') === 1 && !get(this.formControl(), 'value.content.0.content')) ||
      (isString(this.formControl().value) && !doInputGenerateVisibleText(String(this.formControl().value))) ||
      !this.formControl().value
    ) {
      const isDirty = this.formControl().dirty;
      this.formControl().setValue(null);

      if (!isDirty) {
        this.formControl().markAsPristine();
      }
    }
  }

  public static ngxEditorConfigFactory(translate: TranslateService): NgxEditorConfig {
    return {
      locals: {
        altText: translate.instant('textEditor.altText'),
        blockquote: translate.instant('textEditor.blockquote'),
        bold: translate.instant('textEditor.bold'),
        code: translate.instant('textEditor.code'),
        enterValidUrl: translate.instant('textEditor.enterValidUrl'),
        h1: translate.instant('textEditor.h1'),
        h2: translate.instant('textEditor.h2'),
        h3: translate.instant('textEditor.h3'),
        h4: translate.instant('textEditor.h4'),
        h5: translate.instant('textEditor.h5'),
        h6: translate.instant('textEditor.h6'),
        heading: translate.instant('textEditor.heading'),
        insert: translate.instant('textEditor.insert'),
        insertLink: translate.instant('textEditor.insertLink'),
        italic: translate.instant('textEditor.italic'),
        openInNewTab: translate.instant('textEditor.openInNewTab'),
        remove: translate.instant('textEditor.remove'),
        removeLink: translate.instant('textEditor.removeLink'),
        strike: translate.instant('textEditor.strike'),
        text: translate.instant('textEditor.text'),
        title: translate.instant('textEditor.title'),
        underline: translate.instant('textEditor.underline'),
        url: translate.instant('textEditor.url'),
        bullet_list: translate.instant('textEditor.bulletList'),
        background_color: translate.instant('textEditor.backgroundColor'),
        align_center: translate.instant('textEditor.alignCenter'),
        align_justify: translate.instant('textEditor.alignJustify'),
        align_left: translate.instant('textEditor.alignLeft'),
        align_right: translate.instant('textEditor.alignRight'),
        ordered_list: translate.instant('textEditor.orderedList'),
        text_color: translate.instant('textEditor.textColor'),
      },
    };
  }
}
